import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
    Heart, 
    TreePine,
    Star,
    ArrowRight,
    Award
} from 'lucide-react';
import SEOHead, { pageSEO, generateLocalBusinessSchema, generateOrganizationSchema, seoConfig } from '../components/SEOHead';

// Testimonial data
const testimonials = [
    {
        name: 'Sarah T.',
        location: 'Chippenham, Wiltshire',
        text: 'My nervous rescue collie absolutely loved mantrailing with Laura. After just two sessions, I saw a real change in her confidence. Laura is so patient and knowledgeable — I cannot recommend Craven Canines enough!',
        rating: 5,
        dogName: 'Bella'
    },
    {
        name: 'Mark & Jen',
        location: 'Corsham, Wiltshire',
        text: 'We booked the introduction workshop as a birthday treat and it was the best decision. Our Labrador was in his element following the scent trails, and we learnt so much about reading his body language. Brilliant experience!',
        rating: 5,
        dogName: 'Biscuit'
    },
    {
        name: 'Emily R.',
        location: 'Tetbury, Gloucestershire',
        text: "Our high-energy Springer finally has an activity that actually tires him out mentally. Laura's sessions are well-organised, fun, and the locations around Malmesbury are beautiful. We now attend training sessions every month.",
        rating: 5,
        dogName: 'Archie'
    }
];

const HomePage = () => {
    // Combined schema for homepage
    const reviewSchema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": seoConfig.business.name,
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5",
            "reviewCount": String(testimonials.length),
            "bestRating": "5"
        },
        "review": testimonials.map(t => ({
            "@type": "Review",
            "author": { "@type": "Person", "name": t.name },
            "reviewRating": { "@type": "Rating", "ratingValue": String(t.rating), "bestRating": "5" },
            "reviewBody": t.text
        }))
    };

    const homeSchema = [
        generateOrganizationSchema(),
        generateLocalBusinessSchema(),
        reviewSchema
    ];

    return (
        <div className="min-h-screen" data-testid="home-page">
            <SEOHead 
                title={pageSEO.home.title}
                description={pageSEO.home.description}
                keywords={pageSEO.home.keywords}
                canonical={pageSEO.home.canonical}
                schema={homeSchema}
            />
            
            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url('https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/mwde5c6f_DSCF7001.JPG')`
                    }}
                >
                    <div className="hero-overlay absolute inset-0"></div>
                </div>
                
                <div className="container-custom relative z-10 py-20">
                    <div className="max-w-3xl animate-fade-in-up">
                        <div className="flex items-center gap-3 mb-6">
                            <img 
                                src="https://customer-assets.emergentagent.com/job_canine-training-pro/artifacts/k6jr7ouk_Laura%20logo%20CC.jpeg" 
                                alt="Craven Canines - Professional Dog Training Wiltshire" 
                                className="h-16 w-auto bg-white rounded-lg p-1"
                                loading="eager"
                            />
                            <span className="text-primary-foreground/90 font-medium text-lg">Craven Canines</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight font-serif">
                            Professional Mantrailing
                        </h1>
                        <p className="text-xl text-white/90 mb-8 leading-relaxed max-w-2xl">
                            Transform your dog's natural tracking instincts into an exciting skill. Our professional mantrailing sessions near Malmesbury build confidence, 
                            strengthen your bond, and provide incredible mental enrichment for dogs of all breeds.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/mantrailing#booking-section" data-testid="hero-cta-book">
                                <Button className="btn-primary bg-primary hover:bg-primary/90 text-lg px-8 py-6 h-auto">
                                    Book Your Session
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                            <Link to="/mantrailing" data-testid="hero-cta-explore">
                                <Button className="btn-outline border-white text-white hover:bg-white/10 text-lg px-8 py-6 h-auto">
                                    Learn About Mantrailing
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Scroll indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                    <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
                        <div className="w-1.5 h-3 bg-white/70 rounded-full"></div>
                    </div>
                </div>
            </section>

            {/* About Me Section */}
            <section className="section-spacing bg-secondary/30" data-testid="about-section">
                <div className="container-custom">
                    <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <div className="relative">
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                                <img 
                                    src="https://customer-assets.emergentagent.com/job_canine-training-pro/artifacts/nq1rvo79_about%20me%20.jpeg" 
                                    alt="Laura from Craven Canines with her Hungarian Wirehaired Vizsla Margot — professional mantrailing trainer in Wiltshire"
                                    className="w-full h-[500px] object-cover"
                                    loading="lazy"
                                />
                            </div>
                            {/* Decorative elements */}
                            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary/10 rounded-2xl -z-10"></div>
                            <div className="absolute -top-6 -left-6 w-24 h-24 bg-accent/20 rounded-full -z-10"></div>
                        </div>
                        
                        <div className="space-y-6">
                            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
                                <Heart className="w-4 h-4" /> About Me
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-foreground font-serif">
                                Laura and Margot's<br />Mantrailing Journey
                            </h2>
                            <div className="space-y-4 text-muted-foreground leading-relaxed">
                                <p>
                                    I have a Hungarian Wirehaired Vizsla named Margot. I discovered Mantrailing while looking 
                                    for a way to safely channel her strong hunting and scenting instincts. Bred to track and follow 
                                    prey, she was constantly drawn away by exciting smells, making every day walks a real challenge.
                                </p>
                                <p>
                                    Mantrailing transformed that drive into something positive—giving her a clear job to do while 
                                    keeping her focused and fulfilled. By working with her natural instincts rather than trying to 
                                    suppress them, we found an activity that builds control, confidence, and teamwork, and allows 
                                    us to enjoy time together in a way that truly suits her.
                                </p>
                            </div>
                            
                            {/* Trust indicators */}
                            <div className="grid grid-cols-3 gap-4 pt-4">
                                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                                    <TreePine className="w-8 h-8 text-primary mx-auto mb-2" />
                                    <div className="text-sm text-muted-foreground">Scent Work</div>
                                </div>
                                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                                    <Heart className="w-8 h-8 text-primary mx-auto mb-2" />
                                    <div className="text-sm text-muted-foreground">Positive Methods</div>
                                </div>
                                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                                    <Star className="w-8 h-8 text-primary mx-auto mb-2" />
                                    <div className="text-sm text-muted-foreground">All Breeds</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Me - Qualifications Section */}
            <section className="section-spacing" data-testid="about-me-section">
                <div className="container-custom">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-10">
                            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
                                <Award className="w-4 h-4" /> Qualifications & Experience
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-foreground font-serif">
                                About Me
                            </h2>
                        </div>
                        
                        <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
                            <p className="leading-relaxed">
                                I am currently awaiting the results of my Level 5 Advanced Diploma in Applied Animal Behaviour (Canine). 
                                Alongside this qualification, I have completed a certified Canine First Aid training course and have 
                                qualified as a Tier 1 trainer with Scentwork UK, enabling me to deliver safe, structured, and enriching 
                                scentwork sessions.
                            </p>
                            
                            <p className="leading-relaxed">
                                I have been dog walking and pet sitting for many years, and for the past two years I have operated in 
                                a fully insured capacity, specialising in solo walks tailored to each dog's individual needs. My approach 
                                is rooted in positive, ethical training methods, largely shaped by a local behaviourist whom I have 
                                shadowed for over two years to deepen my practical understanding of behaviour cases. My academic work 
                                has also explored canine welfare from a broader ethical perspective; for my university dissertation, 
                                I interviewed Wiltshire Police dog handlers to examine the ethical considerations surrounding the use 
                                of working police dogs.
                            </p>
                            
                            <p className="leading-relaxed">
                                My passion for animals began at a young age, when I became the trusted "go-to" within my village for 
                                caring for a wide variety of pets—from dogs and cats to chickens, horses, hamsters, tortoises, and fish. 
                                At home, I share my life with my almost two-year-old Wirehaired Vizsla, Margot, who has arguably taught 
                                me more about dog behaviour than any qualification or professional experience ever could. Living and 
                                learning alongside her continues to deepen my understanding of breed traits, motivation, arousal levels, 
                                and the realities of navigating adolescence—because every day truly is a learning day. Together we enjoy 
                                mantrailing, agility, detection work, canicross, and long hikes, all of which continually enrich my 
                                practical handling skills and reinforce my belief in providing dogs with appropriate, fulfilling outlets 
                                for both mind and body.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="section-spacing bg-white" data-testid="testimonials-section">
                <div className="container-custom">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
                            <Star className="w-4 h-4" /> What Our Clients Say
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground font-serif">
                            Trusted by Dog Owners Across Wiltshire and Beyond
                        </h2>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {testimonials.map((testimonial, idx) => (
                            <div 
                                key={`testimonial-${idx}`}
                                className="bg-secondary/30 rounded-2xl p-8 relative"
                                data-testid={`testimonial-${idx}`}
                            >
                                <div className="flex gap-1 mb-4">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <Star key={`star-${idx}-${i}`} className="w-5 h-5 fill-amber-400 text-amber-400" />
                                    ))}
                                </div>
                                <p className="text-muted-foreground leading-relaxed mb-6 italic">
                                    "{testimonial.text}"
                                </p>
                                <div className="border-t border-border/50 pt-4">
                                    <p className="font-semibold text-foreground">{testimonial.name}</p>
                                    <p className="text-sm text-muted-foreground">{testimonial.location} — with {testimonial.dogName}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="section-spacing relative overflow-hidden" data-testid="cta-section">
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=1920&q=80')`
                    }}
                >
                    <div className="absolute inset-0 bg-primary/90"></div>
                </div>
                
                <div className="container-custom relative z-10 text-center">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 font-serif">
                        Ready to Start Your Mantrailing Journey?
                    </h2>
                    <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                        Whether you're looking for an introduction or want to take your skills further – 
                        let's find the perfect session for you and your dog!
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/mantrailing#book-session" data-testid="cta-book">
                            <Button className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6 h-auto rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                Book Now
                            </Button>
                        </Link>
                        <Link to="/mantrailing" data-testid="cta-explore">
                            <Button className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 h-auto rounded-full font-semibold transition-all duration-300">
                                View Sessions
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
